import styles from '../document-preview/DocumentPreview.module.scss';

import { Theme, useMediaQuery } from '@mui/material';
import { DateTime } from 'luxon';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  TempFileManagerContext,
  useTempFileManager,
} from '@work4all/data/lib/hooks/data-provider/useTempFileManager';

import { Customer } from '@work4all/models/lib/Classes/Customer.entity';
import { Invoice } from '@work4all/models/lib/Classes/Invoice.entity';
import { RaViewModel } from '@work4all/models/lib/Classes/RaViewModel.entity';
import { ReViewModel } from '@work4all/models/lib/Classes/ReViewModel.entity';
import { ConvertTempfileTarget } from '@work4all/models/lib/Enums/ConvertTempfileTarget.enum';
import { CreateTempFileOriginType } from '@work4all/models/lib/Enums/CreateTempFileOriginType.enum';
import { EInvoiceFormat } from '@work4all/models/lib/Enums/EInvoiceFormat.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { InvoiceForm } from '@work4all/models/lib/Enums/InvoiceForm.enum';
import { RePermitStatus } from '@work4all/models/lib/Enums/RePermitStatus.enum';
import { convertEntityToBzObjType } from '@work4all/models/lib/utils/convertEntityToBzObjType';

import { DateTimeCustom } from '@work4all/utils/lib/date-utils/formatDateString';
import { LanguageResource } from '@work4all/utils/lib/i18n/LanguageResource.enum';

import { MIME_TYPES } from '../../../preview/Preview';
import { Attachments } from '../../attachments';
import { useEntityTemplate } from '../../entity-template-provider/EntityTemplateProvider';
import { ApprovalStatusBanderole } from '../components/approval-banderole/ApprovalStatusBanderole';
import { ActionStatuses } from '../components/approval-banderole/types';
import { PreviewContent } from '../components/PreviewContent';
import { DocumentPreviewContent } from '../document-preview/components/DocumentPreviewContent';
import { DocumentPreviewTitle } from '../document-preview/components/DocumentPreviewTitle';
import { useDocumentPreview } from '../document-preview/hooks/use-document-preview';
import { EntityHistory } from '../EntityHistory';
import { EntityPreviewContainerProps } from '../types';

import { DocumentCopyBanderole } from './components/DocumentCopyBanderole';
import { ErpPreviewTab } from './components/ErpPreviewTab';
import { SimpleReportWithFullScren } from './components/SimpleReportWithFullScreen';
import { useErpPreviewData } from './hooks/use-erp-preview-data';
import { useErpPreviewFields } from './hooks/use-erp-preview-fields';
import { useInboundInvoicePdfEditRights } from './hooks/use-inbound-invoice-pdf-edit-rights';
import { useMutateBzObject } from './hooks/use-mutate-bz-object';
import { usePdfOverlay } from './hooks/use-pdf-overlay';
import { usePreviewNavigation } from './hooks/use-preview-navigation';
import { useSimpleReportActions } from './hooks/use-simple-report-actions';

interface ErpPreviewProps extends EntityPreviewContainerProps {
  entity: Entities;
  languageResources: LanguageResource[];
  previewData: ReturnType<typeof useErpPreviewData>;
  showEditIcon?: boolean;
  showFullscreenIcon?: boolean;
  showDownloadIcon?: boolean;
  isApprovalCenter?: boolean;
}

export function ErpPreview(props: ErpPreviewProps) {
  const {
    entity,
    onCloseClick,
    onEditClicked,
    openInFullscreen = false,
    convertProps,
    languageResources,
    previewData,
    onVisibilityToggle,
    showEditIcon = true,
    showFullscreenIcon = true,
    showDownloadIcon = true,
    isApprovalCenter = false,
  } = props;

  const {
    data: entitiesData,
    refetch,
    fileList,
    requestEntityData,
  } = previewData;

  const { file, component } = usePreviewNavigation(fileList || []);

  const fileInfos = file?.fileInfos;
  const mimeType = file?.mimeType;

  const isSmUp = useMediaQuery<Theme>((t) => t.breakpoints.up('sm'));

  const re =
    entity === Entities.reViewModel
      ? (entitiesData?.[0] as ReViewModel)
      : undefined;

  const inboundInvoicePdfEditAllowed = useInboundInvoicePdfEditRights({
    fileName: file?.fileInfos?.fileEntityFilename,
    documentUserId: entitiesData?.[0]?.userId,
    datevDate: re?.datevDate,
  });

  const docId = useMemo(() => {
    const selectedEntity = re;

    const correctReceipt = selectedEntity?.receipts?.find(
      (x) => x.parentId === file?.id
    );

    return correctReceipt?.id || selectedEntity?.receipts?.[0]?.id;
  }, [file, re]);

  const [showPreview, setShowPreview] = useState(true);

  const reloadPreview = useCallback(async () => {
    await refetch();
    setShowPreview(false);
    setTimeout(() => setShowPreview(true));
  }, [refetch]);

  const { component: pdfOverlay, open: openPdfOverlay } = usePdfOverlay({
    entityId: re?.id,
    entityType: Entities.reViewModel,
    docId,
    onClose: reloadPreview,
  });

  const selectedRowsIdsList = useMemo(() => {
    return entitiesData?.map((x) => x.id) ?? [];
  }, [entitiesData]);

  const showEdit = isSmUp && showEditIcon && entitiesData?.length === 1;

  const subtitleComp = useMemo(() => {
    if (entity === Entities.reViewModel) {
      const data = previewData?.data?.[0];
      const status = data?.permitStatus;

      const userName =
        status === RePermitStatus.APPROVED
          ? data?.releasedByUser?.displayName
          : status === RePermitStatus.REJECTED
          ? data?.blockedByUser?.displayName
          : null;

      const date =
        (status === RePermitStatus.APPROVED ||
          status === RePermitStatus.REJECTED) &&
        data.permitDate
          ? DateTime.fromISO(data.permitDate).toLocaleString(
              DateTimeCustom.DATE_SHORT
            )
          : null;

      return (
        <>
          {!!file?.parentId && <DocumentCopyBanderole />}
          {isApprovalCenter &&
            (status === RePermitStatus.APPROVED ||
              status === RePermitStatus.REJECTED) && (
              <ApprovalStatusBanderole
                userName={userName}
                date={date}
                status={status.toLocaleLowerCase() as ActionStatuses}
              />
            )}
        </>
      );
    }

    return null;
  }, [entity, file?.parentId, isApprovalCenter, previewData?.data]);

  const fields = useErpPreviewFields({
    entity,
    entitiesData,
  });

  const [tab, setTab] = useState<'ORVERVIEW' | 'HISTORY'>('ORVERVIEW');

  const customer: Customer =
    (entitiesData?.[0] as Invoice)?.additionalAddress2?.businessPartner?.data ||
    (entitiesData?.[0] as RaViewModel)?.invoice?.additionalAddress2
      ?.businessPartner?.data;

  const isXInvoice =
    customer?.invoiceForm === InvoiceForm.ELEKTRONISCH &&
    [EInvoiceFormat.X_RECHNUNG_CII, EInvoiceFormat.X_RECHNUNG_UBL].includes(
      customer?.eInvoiceFormat
    );

  const fileUrl = isXInvoice
    ? (entitiesData?.[0] as RaViewModel)?.invoice?.xInvoicePreviewUrl ||
      (entitiesData?.[0] as Invoice)?.xInvoicePreviewUrl
    : fileInfos?.previewUrl;

  const documentPreviewProps = useDocumentPreview(
    isXInvoice ? '' : fileInfos?.fileEntityFilename
  );

  const sharedProps = {
    onCloseClick,
    onEditClicked: showEdit ? onEditClicked : undefined,
    url: fileUrl,
    filePath: isXInvoice ? '' : fileInfos?.fileEntityFilename,
    mimeType: isXInvoice
      ? MIME_TYPES.pdf
      : ((fileInfos?.previewMimeType || mimeType) as MIME_TYPES),
    title:
      entitiesData?.[0]?.note || fileInfos?.fileServiceProviderInfos?.fileName,
    contractNumber: entitiesData?.[0]?.contractNumber || null,
    number: entitiesData?.[0]?.number,
    value: entitiesData?.[0]?.value,
    language: entitiesData?.[0]?.language,
    noPreviewUrl:
      entity !== Entities.reViewModel
        ? fileInfos?.fileServiceProviderInfos?.fspUrl || fileInfos?.downloadUrl
        : fileInfos?.downloadUrl,
    downloadUrl: isXInvoice ? fileUrl : fileInfos?.downloadUrl,
    fspUrl: isXInvoice ? null : fileInfos?.fileServiceProviderInfos?.fspUrl,
    exists: Boolean(fileInfos?.downloadUrl),
  };

  const entityTemplate = useEntityTemplate();

  const url = documentPreviewProps.isLinkable
    ? sharedProps.filePath
    : sharedProps.url;

  const mutate = useMutateBzObject();

  const noBzEntities = [
    Entities.reViewModel,
    Entities.raViewModel,
    Entities.productionContract,
  ];
  const bzObjType = noBzEntities.includes(entity)
    ? null
    : convertEntityToBzObjType(entity);
  const mutateAll = useCallback(
    async (change: object) => {
      const ids = (entitiesData || []).map((x) => x.id);
      await mutate({
        variables: {
          ...change,
          ids,
          bzObjType,
        },
        onCompleted: () => {
          refetch();
        },
      });
    },
    [mutate, entitiesData, bzObjType, refetch]
  );

  const { actions, download, setSimpleReportBlob, fileName } =
    useSimpleReportActions({
      customActions: props.customActions,
      entitiesData,
      entity,
      file,
      setFullscreen: documentPreviewProps.setFullscreen,
      url,
      languageResources,
      showEditIcon,
      showFullscreenIcon,
      showDownloadIcon,
    });

  const cleanedPersistantAttachmentList = useMemo(() => {
    return entitiesData?.[0]?.erpAttachmentList?.map((attachment) => ({
      ...attachment,
      fileName: attachment?.fileInfos?.fileEntityFilename,
      __typename: undefined,
    }));
  }, [entitiesData]);

  const tempFileManager = useTempFileManager(cleanedPersistantAttachmentList, {
    maxAttachmentTotalSize: 50 * 1024 * 1024,
  });

  const data = entitiesData?.[0] ?? {};

  const { t } = useTranslation();

  return (
    <TempFileManagerContext.Provider value={tempFileManager}>
      <React.Fragment>
        {pdfOverlay}
        {showPreview && (
          <div className={styles.wrapper}>
            <DocumentPreviewTitle
              {...documentPreviewProps}
              {...sharedProps}
              entries={entitiesData}
              entity={entity}
              entityData={requestEntityData}
              titleKeyField="note"
              openInFullscreen={openInFullscreen}
              navigation={component}
              erpPreview={true}
              showFullscreenButton={
                ![...(actions.left ?? []), ...(actions.right ?? [])].some(
                  (x) => x.actionKey === 'fullscreen'
                )
              }
              showDownloadButton={showDownloadIcon}
              editPdfTitle={t('COMMON.DIGITAL_NOTES')}
              onPdfEditClicked={
                entity === Entities.reViewModel
                  ? () => openPdfOverlay()
                  : undefined
              }
              editPdfDisabled={!inboundInvoicePdfEditAllowed}
              customActions={actions}
              convertProps={convertProps}
              selectedRowsIdsList={selectedRowsIdsList}
              mutate={mutateAll}
              onVisibilityToggle={onVisibilityToggle}
              multiline
            />
            {subtitleComp}

            {entity !== Entities.raViewModel && (
              <>
                {entitiesData?.length === 1 && (
                  <ErpPreviewTab tab={tab} setTab={setTab} />
                )}
                {tab === 'ORVERVIEW' && (
                  <div
                    style={{
                      borderBottom: '1px solid var(--ui04)',
                      background: 'var(--ui01)',
                      flex: 'unset',
                    }}
                  >
                    <PreviewContent
                      entries={entitiesData || []}
                      entity={entity}
                      fields={fields}
                      mutate={mutateAll}
                    />

                    {tempFileManager.fileList.length > 0 && (
                      <div
                        style={{
                          background: 'var(--textInverse)',
                          padding: '1rem',
                        }}
                      >
                        <Attachments
                          entity={entity}
                          disableAddAction={true}
                          disableRemoveAction={true}
                          layout="compact"
                          disableEditAction={true}
                          convertTempfileTarget={ConvertTempfileTarget.DOKUMENT}
                          createTempFileOriginType={
                            CreateTempFileOriginType.ERP_ATTACHMENT
                          }
                          convertFileAdditionalInfo={{
                            projectId: data.projectId,
                            businessPartnerId: data.businessPartnerId,
                            businessPartnerType: data.businessPartnerType,
                            contactId: data.contactId,
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}

                {tab === 'HISTORY' && entitiesData?.[0] && (
                  <EntityHistory
                    fitContainer={false}
                    entity={mapHistoryEventEntity(entity)}
                    fullWidth
                    id={entitiesData[0].id}
                    className={styles.hist}
                    record={entitiesData[0]}
                  />
                )}
              </>
            )}
            {entity === Entities.reViewModel ||
            entity === Entities.raViewModel ||
            (entity === Entities.productionContract && tab === 'ORVERVIEW') ? (
              <DocumentPreviewContent
                {...documentPreviewProps}
                {...sharedProps}
                iconProps={{
                  showDownloadIcon,
                  showFullscreenIcon,
                }}
              />
            ) : (
              entitiesData?.length === 1 &&
              tab === 'ORVERVIEW' &&
              (url ? (
                <DocumentPreviewContent
                  {...documentPreviewProps}
                  {...sharedProps}
                />
              ) : (
                entitiesData?.[0] && (
                  <SimpleReportWithFullScren
                    onClose={() => documentPreviewProps.setFullscreen(false)}
                    onDownloadClick={download}
                    onEditClicked={sharedProps.onEditClicked}
                    open={documentPreviewProps.detailInFullsceen}
                    fileName={fileName}
                    preview={entityTemplate.renderSimpleReport({
                      data: entitiesData[0],
                      bzObjType,
                      onPdfBlobChange: setSimpleReportBlob,
                    })}
                  />
                )
              ))
            )}
          </div>
        )}
      </React.Fragment>
    </TempFileManagerContext.Provider>
  );
}

export const mapHistoryEventEntity = (entity: Entities) => {
  if (entity === Entities.reViewModel) {
    return Entities.inboundInvoice;
  }
  return entity;
};
